import { HttpMethod } from '@/api/client/http';
import type { Client, File } from '@/components/Views/Company/File/file.types';
import { usePayoffNavigation } from '@/components/Views/Company/Payoff/use-payoff-navigation';
import type { Contact } from '@/interfaces/file-contact/file-contact.types';
import type {
    BorrowerUpdateInfo,
    Lender,
    PayoffOrderFormDisplayDocument,
    PendingClientUpdate,
    PendingContactOperation,
} from '@/interfaces/payoff/payoff-order.types';
import {
    type LenderResponse,
    type PayoffDetailsResponse,
    type PayoffOrder,
    type PayoffOrderFormDisplayData,
    type PayoffOrderFormRequest,
    type PayoffOrdersResponse,
    type PayoffRequestResponse,
    type RequestNewLenderRequest,
    type RequestNewLenderResponse,
} from '@/interfaces/payoff/payoff-order.types';
import type { UploadSellerAuthDocumentResponse } from '@/interfaces/seller-auth/seller-auth-document.types';
import { defineStore } from 'pinia';
import type { DateValue } from 'reka-ui';
import type { Ref } from 'vue';
import { computed, reactive, ref, shallowReactive, watch } from 'vue';
import { useCurrentUserStore } from '../current-user.store';
import { performStoreAction } from './perform-store-action';

const DEFAULT_LIMIT = 100;
const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 100;

export const TIMEOUT_ERROR_MESSAGE = 'We were unable to submit the request to the lender at this time.';

export type PayoffOrderForm = {
    lender?: Lender;
    closingDate?: DateValue;
    loanNumber: string;
    goodThroughDate?: DateValue;
    firstName: string;
    lastName: string;
    ssn: string;
    selectedBorrower?: BorrowerUpdateInfo;
    selectedDocument?: PayoffOrderFormDisplayDocument;
    uploadedDocument?: UploadSellerAuthDocumentResponse['data'];
};

export type PayoffOrderFormErrors = Partial<Record<keyof PayoffOrderForm | 'sellerAuthDocument' | 'borrowerEditor', string>>;

const buildEmptyForm = (): PayoffOrderForm => ({
    lender: undefined,
    closingDate: undefined,
    loanNumber: '',
    goodThroughDate: undefined,
    firstName: '',
    lastName: '',
    ssn: '',
    selectedBorrower: undefined,
    selectedDocument: undefined,
    uploadedDocument: undefined,
});

export const usePayoffOrderStore = defineStore('payoffOrder', () => {
    const currentUserStore = useCurrentUserStore();
    const { fileId } = usePayoffNavigation();

    const currentPayoffId = ref<PayoffOrder['id'] | null>(null);

    // form model + errors — keys are aligned so each component reads/writes the same name.
    // shallowReactive preserves DateValue's class shape that reactive()'s deep unwrap mangles in TS;
    // all form keys are top-level so we don't need deep tracking.
    const form = shallowReactive<PayoffOrderForm>(buildEmptyForm());
    const errors = reactive<PayoffOrderFormErrors>({});
    const formErrorMessage = ref('');

    // collections (not part of the validated form)
    const borrowers = ref<BorrowerUpdateInfo[]>([]);
    const availableDocuments = ref<PayoffOrderFormDisplayDocument[]>([]);

    // tracks whether an inline borrower editor (CardUpdateBorrower) has unsaved changes
    const borrowerEditorDirty = ref(false);

    // submission state
    const isLoading = ref(false);

    // pending operations queue (mutated by use-payoff-queued-operations, flushed on submit)
    const pendingContactOperations = reactive<Map<Contact['borrower_id'], PendingContactOperation>>(new Map());
    const pendingClientUpdates = reactive<Map<Client['id'], PendingClientUpdate>>(new Map());

    const addBorrower = (borrower: BorrowerUpdateInfo) => {
        borrowers.value.push(borrower);
        form.selectedBorrower = borrower;
    };

    const updateBorrower = (borrower: BorrowerUpdateInfo) => {
        const index = borrowers.value.findIndex(b => b.borrower_id === borrower.borrower_id);
        if (index === -1) return;
        borrowers.value[index] = borrower;
        form.selectedBorrower = borrowers.value[index];
    };

    // Feature flag based on account's enable_payoff_retrieval setting
    const isPayoffRetrievalEnabled = computed(() => currentUserStore.currentUserMeta?.enablePayoffRetrieval ?? false);

    const getPayoffs = (fileId: File['id']) => {
        const fetchUrl = computed(() => `/api/user/payoffs/orders?per_page=${DEFAULT_PER_PAGE}&file_id=${fileId}`);

        const {
            data: payoffsData,
            isFetching,
            error: getPayoffsError,
            execute: fetchPayoffs,
        } = performStoreAction<PayoffOrdersResponse>(fetchUrl, HttpMethod.GET, undefined, {
            refetch: true,
            beforeFetch({ cancel }) {
                if (!isPayoffRetrievalEnabled.value) cancel();
            },
        });

        return {
            payoffs: computed(() => payoffsData.value?.data || []),
            getPayoffsError,
            isFetching,
            fetchPayoffs,
        };
    };

    const fetchPayoffDetails = () => {
        const fetchUrl = computed(() => `/api/user/payoffs/details/${currentPayoffId.value}`);

        const {
            data: detailsData,
            isFetching: isLoadingDetails,
            error: detailsError,
            execute: fetchDetails,
        } = performStoreAction<PayoffDetailsResponse>(fetchUrl, HttpMethod.GET, undefined, {
            refetch: true,
            beforeFetch({ cancel }) {
                if (!currentPayoffId.value) cancel();
            },
        });

        return {
            payoff: computed(() => detailsData.value?.data || null),
            isLoadingDetails,
            detailsError,
            fetchDetails,
        };
    };

    const getLenders = (page = DEFAULT_PAGE, limit = DEFAULT_LIMIT) => {
        const { data: lenderData, isFetching } = performStoreAction<LenderResponse>(
            `/api/user/payoffs/lenders?page=${page}&limit=${limit}`,
            HttpMethod.GET,
        );

        return {
            lenders: computed(() => lenderData.value?.data ?? []),
            isFetching,
        };
    };

    const searchLenders = (searchQuery: Ref<string>, page = DEFAULT_PAGE, limit = DEFAULT_LIMIT) => {
        const fetchUrl = computed(
            () => `/api/user/payoffs/lenders/search?query=${searchQuery.value}&page=${page}&limit=${limit}`,
        );

        const { data: lenderData, isFetching: isSearchingLenders } = performStoreAction<LenderResponse>(
            fetchUrl,
            HttpMethod.GET,
            undefined,
            {
                refetch: true,
                beforeFetch({ cancel }) {
                    if (!searchQuery.value?.trim()) cancel();
                },
            },
        );

        return {
            lenderSearchResults: computed(() => lenderData.value?.data ?? []),
            isSearchingLenders,
        };
    };

    const requestLender = async (payload: RequestNewLenderRequest) => {
        const { error: requestLenderError, isFetching } = await performStoreAction<
            RequestNewLenderResponse,
            RequestNewLenderRequest
        >(`/api/user/payoffs/lenders/request`, HttpMethod.POST, payload);

        return { requestLenderError, isFetching };
    };

    const getFormInitialData = () => {
        const hasValidFileId = () => Number.isFinite(fileId.value) && fileId.value > 0;
        const fetchUrl = computed(() => (hasValidFileId() ? `/api/user/payoffs/form-data/${fileId.value}` : ''));

        const { data, isFetching } = performStoreAction<PayoffOrderFormDisplayData>(fetchUrl, HttpMethod.GET, undefined, {
            refetch: true,
            beforeFetch(ctx) {
                if (!hasValidFileId()) ctx.cancel();
            },
        });

        return {
            initialData: computed(() => data.value),
            isFetching,
        };
    };

    const submitPayoffOrder = (payload: PayoffOrderFormRequest) => {
        return performStoreAction<PayoffRequestResponse, PayoffOrderFormRequest>(
            '/user/payoffs/orders',
            HttpMethod.POST,
            payload,
        );
    };

    const initialFormData = getFormInitialData();

    // hydrate borrowers and available documents from server response
    watch(
        initialFormData.initialData,
        initialData => {
            borrowers.value = initialData?.borrowers ?? [];
            availableDocuments.value = initialData?.available_documents ?? [];
        },
        { deep: true },
    );

    const resetForm = () => {
        Object.assign(form, buildEmptyForm());
        Object.keys(errors).forEach(key => {
            delete errors[key as keyof PayoffOrderFormErrors];
        });

        formErrorMessage.value = '';
        borrowerEditorDirty.value = false;
        isLoading.value = false;

        borrowers.value = initialFormData.initialData.value?.borrowers ?? [];
        availableDocuments.value = initialFormData.initialData.value?.available_documents ?? [];
    };

    const lenderTurnaroundTimeInDays = computed(() => {
        // turnaround_time from the BE is guaranteed to be at least 72 hours
        return Math.ceil((form.lender?.turnaround_time ?? 0) / 24);
    });

    return {
        // Feature flag
        isPayoffRetrievalEnabled,

        // Payoff orders table
        getPayoffs,

        // Payoff details sheet
        currentPayoffId,
        fetchPayoffDetails,

        // Select a lender
        getLenders,
        searchLenders,
        requestLender,

        // Form fetch / submission
        getFormInitialData,
        initialFormData,
        submitPayoffOrder,

        // form state
        form,
        errors,
        formErrorMessage,

        // collections
        borrowers,
        availableDocuments,

        // submission state
        isLoading,
        borrowerEditorDirty,

        // borrower mutators
        addBorrower,
        updateBorrower,

        // reset form
        resetForm,

        // pending operation queues (mutated/consumed by the queued-operations composable)
        pendingContactOperations,
        pendingClientUpdates,

        // lender
        lenderTurnaroundTimeInDays,
    };
});
